import React, { useState } from "react";
import axios from "axios";
import { Zap, Lock, Target, DollarSign, MessageCircle, Send, Loader2, CheckCircle2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { BRAND, waLink } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  { icon: Zap, text: "Reply within 2 hours (IST)" },
  { icon: Lock, text: "Your data is 100% private" },
  { icon: Target, text: "Free consultation included" },
  { icon: DollarSign, text: "No hidden charges or commitment" },
];
const STATS = [["2 hrs", "Avg Response"], ["20K+", "Happy Traders"], ["500+", "Projects Done"], ["4.6★", "Avg Rating"]];

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", whatsapp: "", email: "", interest: "Gold EA Purchase", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.whatsapp) {
      toast({ title: "Missing details", description: "Please enter your name and WhatsApp number." });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setLoading(false);
      setDone(true);
      toast({ title: "Request received!", description: "Our team will contact you on WhatsApp within 2 hours." });
      setForm({ name: "", whatsapp: "", email: "", interest: "Gold EA Purchase", message: "" });
      setTimeout(() => setDone(false), 3000);
    } catch (err) {
      setLoading(false);
      toast({ title: "Something went wrong", description: "Please try again or reach us on WhatsApp." });
    }
  };

  const inputCls = "w-full bg-[#0a1424] border border-[rgba(46,155,255,0.2)] rounded-xl px-4 py-3 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-[#2e9bff] focus:ring-2 focus:ring-[rgba(46,155,255,0.25)] transition";

  return (
    <section id="contact" className="py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center reveal">
          <span className="text-[12px] tracking-[0.25em] text-[#2e9bff] font-semibold">FREE CONSULTATION</span>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-white mt-4">Get in Touch — <span className="text-blue-grad">We'll Contact You</span></h2>
          <p className="text-slate-400 mt-4 max-w-2xl mx-auto">Fill in your details and our team will reach out via WhatsApp within 2 hours with a personalised plan just for you.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-14 items-start">
          <div className="reveal space-y-6">
            <div className="glass-card rounded-2xl p-7">
              <h3 className="text-white font-semibold text-lg">Why Share Your Details?</h3>
              <div className="space-y-4 mt-5">
                {REASONS.map((r) => (
                  <div key={r.text} className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg grid place-items-center bg-[rgba(46,155,255,0.12)] border border-[rgba(46,155,255,0.25)]"><r.icon className="w-4 h-4 text-[#2e9bff]" /></div>
                    <span className="text-slate-300 text-sm">{r.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {STATS.map(([v, l]) => (
                <div key={l} className="glass-card rounded-xl p-5 text-center">
                  <div className="font-display text-2xl font-bold text-blue-grad">{v}</div>
                  <p className="text-slate-400 text-xs mt-1">{l}</p>
                </div>
              ))}
            </div>
            <a href={waLink("Hi, I'd like a free consultation")} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 py-3.5 rounded-full font-semibold bg-[#22e39a] text-[#04140c] hover:brightness-105 transition">
              <MessageCircle className="w-4 h-4" /> Or Chat Directly on WhatsApp
            </a>
          </div>

          <form onSubmit={submit} className="reveal glass-card rounded-2xl p-7">
            <h3 className="text-white font-semibold text-lg mb-5">Get Your Free Consultation</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-300">Full Name *</label>
                <input className={inputCls + " mt-1.5"} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Doe" />
              </div>
              <div>
                <label className="text-sm text-slate-300">WhatsApp Number *</label>
                <input className={inputCls + " mt-1.5"} value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+91 90000 00000" />
              </div>
              <div>
                <label className="text-sm text-slate-300">Email (optional)</label>
                <input className={inputCls + " mt-1.5"} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" />
              </div>
              <div>
                <label className="text-sm text-slate-300">I am interested in *</label>
                <select className={inputCls + " mt-1.5"} value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })}>
                  <option>Gold EA Purchase</option>
                  <option>Custom Software</option>
                  <option>Both / Not Sure Yet</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-300">Message (optional)</label>
                <textarea rows={3} className={inputCls + " mt-1.5 resize-none"} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your goals..." />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full py-3.5 rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-70">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : done ? <><CheckCircle2 className="w-4 h-4" /> Sent!</> : "Get Free Consultation"}
              </button>
              <p className="text-slate-500 text-xs text-center">By submitting, you agree to our <a href="#terms" className="text-[#2e9bff] hover:underline">Terms & Conditions</a>. We respect your privacy.</p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
